import React, { createContext, useContext, useState, ReactNode, useRef } from "react";

export interface NotificationObject {
    contextName?: string;
    action?: string;
    ruleTagName?: string;
    recvTime?: string;
    read?: boolean;
    trend?: 'up' | 'down';
    attr?: string;
    attributeKey?: string;
    user?: string;
}

interface NotificationContextProps {
  notifications: NotificationObject[];
  addNotification: (notification: NotificationObject) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotificationContext debe ser utilizado dentro de NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationObject[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  const addNotification = (notification: NotificationObject) => {
    // Generar un id estable para evitar duplicados
    const id = [
      notification.user || "",
      notification.contextName || "",
      notification.ruleTagName || "",
      notification.recvTime || "",
    ].join("|");

    if (seenRef.current.has(id)) {
      return; // Ignorar duplicado
    }
    seenRef.current.add(id);
    setNotifications((prevNotifications) => [notification, ...prevNotifications]);
  };

  const clearNotifications = () => {
    setNotifications([]);
    seenRef.current.clear();
  };

  const contextValue: NotificationContextProps = {
    notifications,
    addNotification,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
